import type { TourSection } from './types';

// Tour de /admin/activation-rules. Las pestañas Borrador, Vigentes e
// Historial se abren solas antes de cada step (patrón `openTab`).
function openTab(key: string): () => Promise<void> {
  return async () => {
    const btn = document.querySelector<HTMLButtonElement>(
      `[data-tour="activation-rules-tab-${key}"]`,
    );
    if (btn && btn.getAttribute('aria-selected') !== 'true') {
      btn.click();
      // 2 frames para que React monte el panel tras setState + URL update.
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
      );
    }
  };
}

export const activationRulesSections: TourSection[] = [
  {
    id: 'status',
    label: 'Estado de las reglas',
    steps: [
      {
        target: '[data-tour="activation-rules-status"]',
        title: 'Estado',
        content:
          'Muestra si hay reglas vigentes y cuántos productos están apagados por regla en Activo y en Activo en web. Las reglas nunca prenden algo que la fuente o una persona apagó.',
      },
      {
        target: '[data-tour="activation-rules-deactivate"]',
        title: 'Desactivar reglas',
        content:
          'Es el freno: las reglas dejan de regir y cada producto que apagaron vuelve a su valor anterior. Para usarlas de nuevo hay que simular y activar otra vez.',
      },
    ],
  },
  {
    id: 'editor',
    label: 'Armar el borrador',
    steps: [
      {
        target: '[data-tour="activation-rules-steps"]',
        title: 'Pasos',
        content:
          'El borrador va en tres pasos: armar y guardar las reglas, simularlas y activarlas. El paso resaltado es el que sigue.',
        before: openTab('draft'),
      },
      {
        target: '[data-tour="activation-rules-list-permit"]',
        title: 'Permitir si…',
        content:
          'El producto tiene que cumplir al menos una de estas reglas para poder estar activo. Cada regla tiene un nombre, a qué campo aplica y hasta 5 condiciones que se cumplen todas.',
        before: openTab('draft'),
      },
      {
        target: '[data-tour="activation-rules-list-block"]',
        title: 'Bloquear si…',
        content: 'Si el producto cumple alguna de estas reglas, se apaga sin importar lo demás.',
        before: openTab('draft'),
      },
      {
        target: '[data-tour="activation-rules-list-permit"]',
        title: 'De qué fuente se lee cada campo',
        content:
          'Debajo de cada campo dice de qué fuentes lo leen las reglas. Con "Cambiar" eliges las fuentes y su orden; vale para todas las reglas que usan ese campo. Gana la primera fuente que tiene el dato (el 0 y el valor falso cuentan como dato); si ninguna lo tiene, el campo queda vacío. Las listas, como reemplazos o sustitutos, no se juntan entre fuentes. Cambiarlo obliga a simular de nuevo.',
        before: openTab('draft'),
      },
      {
        target: '[data-tour="activation-rules-save"]',
        title: 'Guardar borrador',
        content:
          'Guarda el borrador completo. Guardar no cambia ningún producto ni las reglas vigentes.',
        before: openTab('draft'),
      },
    ],
  },
  {
    id: 'simulation',
    label: 'Simular y activar',
    steps: [
      {
        target: '[data-tour="activation-rules-simulation"]',
        title: 'Simular',
        content:
          'Recorre el catálogo con el borrador guardado y cuenta cuántos productos se apagarían o se soltarían, sin cambiar ninguno. Puedes descargar la lista completa.',
        before: openTab('draft'),
      },
      {
        target: '[data-tour="activation-rules-activate"]',
        title: 'Activar',
        content:
          'Solo se activa lo que se simuló en las últimas 24 horas. Antes de confirmar se repiten los números de la simulación. El cambio llega a los productos en segundo plano.',
        before: openTab('draft'),
      },
    ],
  },
  {
    id: 'active',
    label: 'Reglas vigentes',
    steps: [
      {
        target: '[data-tour="activation-rules-active"]',
        title: 'Vigentes',
        content:
          'Las reglas que rigen hoy, de solo lectura, con la fuente de cada campo que la tiene elegida. Si una fuente aparece como no activa, las reglas dejan de aplicarse a los productos hasta que esa fuente vuelva o se active un borrador sin ella.',
        before: openTab('active'),
      },
    ],
  },
  {
    id: 'manual',
    label: 'Cambios manuales',
    steps: [
      {
        target: '[data-tour="activation-rules-manual"]',
        title: 'Cambios manuales',
        content:
          'Los productos a los que alguien les prendió o apagó a mano el activo o el activo en web. Mientras dura el cambio las reglas no tocan ese campo; vence a las 10:00 del día siguiente y ahí deciden las reglas con los datos de ese momento. Arriba ves cuándo es el próximo vencimiento y cuántos cambios vencen.',
        before: openTab('manual'),
      },
    ],
  },
  {
    id: 'history',
    label: 'Historial',
    steps: [
      {
        target: '[data-tour="activation-rules-history"]',
        title: 'Historial',
        content:
          'Los juegos de reglas que estuvieron vigentes, del más nuevo al más viejo. Ábrelos para ver sus reglas.',
        before: openTab('history'),
      },
    ],
  },
];
